import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Select, 
  InputNumber, 
  Button, 
  Table, 
  Space, 
  message, 
  Row, 
  Col,
  Tag,
  Popconfirm,
  DatePicker
} from 'antd';
import {
  SaveOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  TeamOutlined
} from '@ant-design/icons';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import axios from 'axios';
import { getHeaders } from '../../utility/auth';
import dayjs from 'dayjs';
import fetchData from '../../functions/fetchData';

const { Option } = Select;

const PromoterEntry = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [promoters, setPromoters] = useState([]);
  const [entries, setEntries] = useState([]);
  const [setupDate, setSetupDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  // Fetch setup date from API
  const fetchSetupDate = async () => {
    try {
      const headers = getHeaders();
      const response = await axios.get('/admin/dashboard/setup-date', headers);
      if (response.data.success && response.data.data) {
        setSetupDate(response.data.data.setup_date);
        return response.data.data.setup_date;
      }
    } catch (error) {
      console.error('Error fetching setup date:', error);
      message.error('Failed to load setup date');
      // Fallback to today's date if API fails
      const today = new Date().toISOString().split('T')[0];
      setSetupDate(today);
      return today;
    }
  };

  // Initial load - fetch setup date and set it as selected date
  useEffect(() => {
    const initializeData = async () => {
      const setupDt = await fetchSetupDate();
      if (setupDt) {
        setSelectedDate(dayjs(setupDt));
      }
      fetchPromoters();
    };
    initializeData();
  }, []);

  // Fetch entries when selected date changes
  useEffect(() => {
    if (selectedDate) {
      fetchEntries();
    }
  }, [selectedDate]);

  const fetchPromoters = async () => {
    try {
      const data = await fetchData('promotors', null, 'name', 'flag=1');
      // console.log('Promoters response:', data);
      if (data && Array.isArray(data)) {
        // console.log('Promoters data:', data);
        // console.log('Number of promoters:', data.length);
        // console.log('First promoter:', data[0]);
        setPromoters(data);
      } else {
        // console.log('No data in response');
        setPromoters([]);
      }
    } catch (error) {
      console.error('Error fetching promoters:', error);
      message.error('Failed to load promoters');
      setPromoters([]);
    }
  };

  const fetchEntries = async () => {
    setTableLoading(true);
    try {
      const formattedDate = selectedDate.format('YYYY-MM-DD');
      const data = await fetchData('promotor_entry', null, 'id', { dte: formattedDate });
      if (data && Array.isArray(data)) {
        // Sort by id in descending order (latest entries first)
        const sortedData = data.sort((a, b) => b.id - a.id);
        setEntries(sortedData);
      } else {
        setEntries([]);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      message.error('Failed to load entries');
      setEntries([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const selectedPromoter = promoters.find(p => p.id === values.promoter_id);
      const promoterName = selectedPromoter?.name || '';
      
      const payload = {
        name: promoterName,
        nos: values.no_of_persons,
        dte: selectedDate.format('YYYY-MM-DD'),
        tme: new Date().toLocaleTimeString('en-GB', { hour12: false }),
        flag: 1
      };

      const response = await axios.post('/insertdata/promotor_entry', payload, getHeaders());
      
      if (response.status === 200 || response.status === 201 || (response.data && response.data.success)) {
        message.success('Promoter entry saved successfully');
        form.resetFields();
        fetchEntries(); // Refresh the table
      } else {
        message.error(response.data?.message || 'Failed to save entry');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      message.error(error.response?.data?.message || 'Failed to save entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`/promotor_entry/${id}`, getHeaders());
      if (response.data && response.data.success) {
        message.success('Entry deleted successfully');
        fetchEntries();
      } else {
        message.error('Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      message.error('Failed to delete entry');
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Promoter Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (name) => {
        return (
          <Space>
            <UserOutlined />
            <span>{name || 'Unknown'}</span>
          </Space>
        );
      },
    },
    {
      title: 'No. of Persons',
      dataIndex: 'nos',
      key: 'nos',
      align: 'center',
      sorter: (a, b) => a.nos - b.nos,
      render: (text) => (
        <Tag color="blue" icon={<TeamOutlined />}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Entry Date',
      dataIndex: 'dte',
      key: 'dte',
      sorter: (a, b) => new Date(a.dte) - new Date(b.dte),
      defaultSortOrder: 'descend',
      render: (text) => dayjs(text).format('DD-MM-YYYY'),
    },
    {
      title: 'Time',
      dataIndex: 'tme',
      key: 'tme',
      sorter: (a, b) => (a.tme || '').localeCompare(b.tme || ''),
    },
    
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      width: 100,
      render: (text, record) => (
        <Popconfirm
          title="Delete Entry"
          description="Are you sure you want to delete this entry?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button 
            type="primary" 
            danger 
            size="small"
            icon={<DeleteOutlined />}
          >
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Layout>
      <Header title="Promoter Entry" />
      <div className="wrapper" style={{ padding: '20px' }}>
        {/* Entry Form Card - Horizontal Layout */}
        <Card 
         
          bordered={false}
        >
          <div style={{ 
            background: 'rgba(255,255,255,0.95)', 
            padding: '24px', 
            borderRadius: '8px' 
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #f8f7c4ff'
            }}>
              <UserOutlined style={{ fontSize: '24px', color: '#667eea', marginRight: '12px' }} />
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>New Promoter Entry</span>
            </div>
            
            <Form
              form={form}
              layout="inline"
              onFinish={handleSubmit}
              autoComplete="off"
              style={{ width: '100%' }}
            >
              <Row gutter={16} style={{ width: '100%' }}>
                <Col xs={24} sm={8} md={6}>
                  <Form.Item
                    label="Promoter Name"
                    name="promoter_id"
                    rules={[{ required: true, message: 'Please select a promoter' }]}
                    style={{ width: '100%', marginBottom: '16px' }}
                  >
                    <Select
                      showSearch
                      placeholder="Select promoter"
                      optionFilterProp="children"
                      style={{ width: '100%' }}
                      size="large"
                      filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {/* console.log('Rendering dropdown, promoters:', promoters) */}
                      {promoters && promoters.length > 0 ? (
                        promoters.map((promoter) => {
                         // console.log('Mapping promoter:', promoter);
                          return (
                            <Option key={promoter.id} value={promoter.id}>
                              {promoter.name || 'Unnamed'}
                            </Option>
                          );
                        })
                      ) : (
                        <Option disabled>No promoters available</Option>
                      )}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8} md={6}>
                  <Form.Item
                    label="No. of Persons"
                    name="no_of_persons"
                    rules={[
                      { required: true, message: 'Please enter number of persons' },
                      { type: 'number', min: 1, message: 'Must be at least 1' }
                    ]}
                    style={{ width: '100%', marginBottom: '16px' }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Enter number"
                      min={1}
                      max={9999}
                      size="large"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8} md={6}>
                  <Form.Item style={{ width: '100%', marginBottom: '16px' }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={loading}
                      size="large"
                      block
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        height: '40px',
                        fontWeight: 'bold',
                        marginTop: '30px'
                      }}
                    >
                      Save Entry
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </Card>

        {/* Current Entries Table Card */}
        <Card
          
          bordered={false}
        >
          <div style={{ 
            background: 'rgba(255,255,255,0.95)', 
            padding: '24px', 
            borderRadius: '8px' 
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #f5576c'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TeamOutlined style={{ fontSize: '24px', color: '#f5576c', marginRight: '12px' }} />
                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>Current Date Entries</span>
                <Tag color="green" style={{ marginLeft: '12px', fontSize: '14px' }}>
                  {selectedDate.format('DD-MM-YYYY')}
                </Tag>
              </div>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchEntries}
                loading={tableLoading}
                style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  border: 'none',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                Refresh
              </Button>
            </div>
            
            <Table
              columns={columns}
              dataSource={entries}
              rowKey="id"
              loading={tableLoading}
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} entries`,
                showSizeChanger: true,
              }}
              scroll={{ x: 800 }}
              size="middle"
              style={{
                background: 'white',
                borderRadius: '8px'
              }}
            />
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default PromoterEntry;
